<template>
  <div>
    <b-container fluid>
      <b-row>
        <b-col>
          <h2>Gerenciar Termos</h2>

          <!-- Current Published Term Alert -->
          <b-alert v-if="currentPublishedTerm" show variant="success" class="mt-4">
            <h5 class="alert-heading">Termo Publicado Atual</h5>
            <p class="mb-0">
              <strong>ID:</strong> {{ currentPublishedTerm.id }} |
              <strong>Atualizado em:</strong> {{ formatDate(currentPublishedTerm.updated_at) }}
            </p>
          </b-alert>

          <!-- Filters -->
          <b-row class="mt-4">
            <b-col cols="12" md="6">
              <b-form-group label="Filtrar por Status:" label-for="filter-status">
                <b-form-select id="filter-status" v-model="filterStatus" :options="statusOptions"></b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Filtrar por Categoria:" label-for="filter-category">
                <b-form-select id="filter-category" v-model="filterCategory" :options="categoryOptions"></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Terms Table -->
          <b-table
            striped
            hover
            class="mt-4"
            :items="filteredTerms"
            :fields="fields"
            primary-key="id"
            sort-icon-left
            responsive="md"
            :busy="loading"
          >
            <template v-slot:table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Carregando...</strong>
              </div>
            </template>

            <template v-slot:cell(status)="data">
              <b-badge :variant="getStatusVariant(data.item.status)">
                {{ getStatusLabel(data.item.status) }}
              </b-badge>
            </template>

            <template v-slot:cell(category)="data">
              {{ getCategoryLabel(data.item.category) }}
            </template>

            <template v-slot:cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>

            <template v-slot:cell(updated_at)="data">
              {{ formatDate(data.item.updated_at) }}
            </template>

            <template v-slot:cell(actions)="data">
              <div class="text-nowrap">
                <!-- View Button -->
                <b-button
                  size="sm"
                  variant="info"
                  class="mr-1"
                  @click="viewTerm(data.item)"
                  v-b-tooltip.hover
                  title="Visualizar"
                >
                  <icon name="eye" class="mx-0"></icon>
                </b-button>

                <!-- Edit Button -->
                <span v-if="data.item.status === 'draft'">
                  <b-button
                    size="sm"
                    variant="primary"
                    class="mr-1"
                    @click="editTerm(data.item.id)"
                    v-b-tooltip.hover
                    title="Editar"
                  >
                    <icon name="edit" class="mx-0"></icon>
                  </b-button>
                </span>
                <span v-else>
                  <b-button
                    size="sm"
                    variant="secondary"
                    class="mr-1"
                    disabled
                    v-b-tooltip.hover
                    title="Não é possível editar termos publicados ou depreciados. Crie um novo rascunho."
                  >
                    <icon name="edit" class="mx-0"></icon>
                  </b-button>
                </span>

                <!-- Publish Button (only for draft) -->
                <b-button
                  v-if="data.item.status === 'draft'"
                  size="sm"
                  variant="success"
                  @click="publishTerm(data.item)"
                  v-b-tooltip.hover
                  title="Publicar"
                >
                  <icon name="check" class="mx-0"></icon>
                </b-button>
              </div>
            </template>
          </b-table>

          <!-- No results message -->
          <b-alert v-if="!loading && filteredTerms.length === 0" show variant="warning" class="mt-3">
            Nenhum termo encontrado com os filtros selecionados.
          </b-alert>
        </b-col>
      </b-row>
    </b-container>

    <!-- View Term Modal -->
    <b-modal
      id="view-term-modal"
      :title="'Termo #' + (viewingTerm ? viewingTerm.id : '')"
      size="lg"
      ok-only
      ok-title="Fechar"
    >
      <div v-if="viewingTerm">
        <p><strong>ID:</strong> {{ viewingTerm.id }}</p>
        <p><strong>Categoria:</strong> {{ getCategoryLabel(viewingTerm.category) }}</p>
        <p>
          <strong>Status:</strong>
          <b-badge :variant="getStatusVariant(viewingTerm.status)">
            {{ getStatusLabel(viewingTerm.status) }}
          </b-badge>
        </p>
        <p><strong>Criado em:</strong> {{ formatDate(viewingTerm.created_at) }}</p>
        <p><strong>Atualizado em:</strong> {{ formatDate(viewingTerm.updated_at) }}</p>
        <hr>
        <p><strong>Conteúdo:</strong></p>
        <div v-html="viewingTerm.body" class="border p-3" style="max-height: 400px; overflow-y: auto;"></div>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'TermsList',
  data () {
    return {
      loading: true,
      terms: [],
      currentPublishedTerm: null,
      filterStatus: 'all',
      filterCategory: 'all',
      viewingTerm: null,
      statusOptions: [
        { value: 'all', text: 'Todos' },
        { value: 'draft', text: 'Rascunho' },
        { value: 'published', text: 'Publicado' },
        { value: 'deprecated', text: 'Depreciado' }
      ],
      categoryOptions: [
        { value: 'all', text: 'Todas' },
        { value: 'purchase_terms', text: 'Termos de Compra' }
      ],
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'category', label: 'Categoria', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'created_at', label: 'Criado em', sortable: true },
        { key: 'updated_at', label: 'Atualizado em', sortable: true },
        { key: 'actions', label: 'Ações', class: 'text-center' }
      ]
    }
  },
  computed: {
    filteredTerms () {
      let filtered = this.terms

      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(term => term.status === this.filterStatus)
      }

      if (this.filterCategory !== 'all') {
        filtered = filtered.filter(term => term.category === this.filterCategory)
      }

      return filtered
    }
  },
  async mounted () {
    await this.fetchTerms()
  },
  methods: {
    async fetchTerms () {
      this.loading = true
      try {
        const response = await this.$http.get(process.env.VUE_APP_URL_APP + '/b/admin/terms')
        this.terms = response.body.terms || []

        // Find current published term
        this.currentPublishedTerm = this.terms.find(term =>
          term.category === 'purchase_terms' && term.status === 'published'
        )
      } catch (err) {
        console.error(err)
        this.$bvToast.toast('Erro ao carregar termos', {
          title: 'Erro',
          autoHideDelay: 5000,
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    getStatusVariant (status) {
      const variants = {
        draft: 'warning',
        published: 'success',
        deprecated: 'secondary'
      }
      return variants[status] || 'secondary'
    },
    getStatusLabel (status) {
      const labels = {
        draft: 'Rascunho',
        published: 'Publicado',
        deprecated: 'Depreciado'
      }
      return labels[status] || status
    },
    getCategoryLabel (category) {
      const labels = {
        purchase_terms: 'Termos de Compra'
      }
      return labels[category] || category
    },
    formatDate (date) {
      if (!date) return '-'
      return this.$moment(date).format('DD/MM/YYYY HH:mm')
    },
    editTerm (termId) {
      this.$router.push({ name: 'AdminTermsEdit', params: { id: termId } })
    },
    viewTerm (term) {
      this.viewingTerm = term
      this.$bvModal.show('view-term-modal')
    },
    async publishTerm (term) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Tem certeza que deseja publicar este termo? O termo publicado atual será automaticamente depreciado.',
        {
          title: 'Confirmar Publicação',
          okVariant: 'success',
          cancelVariant: 'secondary',
          okTitle: 'Sim, publicar',
          cancelTitle: 'Cancelar',
          centered: true
        }
      )

      if (!confirmed) {
        return
      }

      try {
        await this.$http.put(process.env.VUE_APP_URL_APP + `/b/admin/terms/${term.id}/publish`)

        this.$bvToast.toast('Termo publicado com sucesso!', {
          title: 'Sucesso',
          autoHideDelay: 5000,
          variant: 'success',
          toaster: 'b-toaster-top-center',
          solid: true
        })

        // Reload terms
        await this.fetchTerms()
      } catch (err) {
        console.error(err)
        const errorMessage = err.body?.message || 'Erro ao publicar termo'
        this.$bvToast.toast(errorMessage, {
          title: 'Erro',
          autoHideDelay: 5000,
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          solid: true
        })
      }
    }
  }
}
</script>
